import React, { useState, memo, useCallback } from 'react';
import ReactFlow, { Handle, Position, useEdges, Controls } from 'reactflow';
import 'reactflow/dist/style.css';

import { career_data as carrera1 } from './data/itecad_mesh';
import { career_data as carrera2 } from './data/isi_mesh';
import { career_data as carrera3 } from './data/topo_mesh';
import { career_data as carrera4 } from './data/iciagro_mesh';
import { career_data as carrera5 } from './data/ictp_mesh';

// **Configuración de colores y estilos**
const COLORS = {
    semesters: [
        "rgba(220, 210, 245, 0.40)",   // Áreas disciplinares
        "rgba(240, 248, 255, 0.70)",   // Año 1-1
        "rgba(220, 240, 255, 0.70)",   // Año 1-2
        "rgba(200, 230, 255, 0.70)",   // Año 2-1
        "rgba(180, 220, 235, 0.70)",   // Año 2-2
        "rgba(160, 210, 220, 0.70)",   // Año 3-1
        "rgba(140, 200, 200, 0.70)",   // Año 3-2
        "rgba(120, 190, 180, 0.70)",   // Año 4-1
        "rgba(100, 180, 160, 0.50)",   // Año 4-2
        "rgba(255, 255, 255, 0.90)",   // Optativos
    ],
    areas: {
        0: "#93dff4",  // Ciencia de datos
        1: '#F8BBD0',  // Inteligencia Artificial
        2: '#D1C4E9',  // Tecnologías computacionales
        3: '#C8E6C9',  // Matemáticas
        4: '#FFF9C4',  // Ciencias complementarias
        5: '#FFFFFF'   // Optativos
    }
};

const SEMESTER_TITLES = {
    "0 0": "Áreas Disciplinares",
    "1 1": "Primer Año - I Ciclo",
    "1 2": "Primer Año - II Ciclo",
    "2 1": "Segundo Año - I Ciclo",
    "2 2": "Segundo Año - II Ciclo",
    "3 1": "Tercer Año - I Ciclo",
    "3 2": "Tercer Año - II Ciclo",
    "4 1": "Cuarto Año - I Ciclo",
    "4 2": "Cuarto Año - II Ciclo",
    "5 1": "Quinto Año - I Ciclo",
    "5 2": "Quinto Año - II Ciclo",
    "Opt": "Optativos"
};

// **Estilos base para nodos**
const NODE_STYLES = {
    container: {
        width: '180px',
        minHeight: '80px',
        backgroundColor: 'white',
        borderRadius: '8px',
        border: '1px solid #E2E8F0',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        padding: '12px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        position: 'relative',
        transition: 'all 0.3s ease'
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '6px'
    },
    emoji: {
        width: '24px',
        height: '24px',
        borderRadius: '50%',
        backgroundColor: '#F1F5F9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    title: {
        fontSize: '14px',
        fontWeight: '600',
        color: '#1E293B'
    },
    code: {
        fontSize: '16px',
        color: 'rgb(105 1 1)',
        textAlign: 'center',
        display: 'block',
        
    }
};

// **Título de la carrera**
const CarreraTitle = ({ nombre }) => (
    <div style={{
        textAlign: 'center',
        margin: '20px 0',
        padding: '15px',
        backgroundColor: '#2c3e50',
        color: 'white',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        fontSize: '24px',
        fontWeight: 'bold'
    }}>
        {nombre}
    </div>
);

// **Componente de nodo personalizado**
const UniformNode = memo(({ id, data, selected }) => {
    const edges = useEdges();
    const hasConnections = edges.some(e =>
        (e.source === id || e.target === id) && !e.hidden
    );

    return (
        <div style={{
            ...NODE_STYLES.container,
            backgroundColor: COLORS.areas[data.area] || '#FFFFFF',
            opacity: selected ? 1 : 0.3,
            zIndex: selected ? 20 : 1,
            border: selected ? '2px solid #3B82F6' : '1px solid #E2E8F0',
            boxShadow: selected ? '0 0 15px rgba(59, 130, 246, 0.5)' : '0 2px 4px rgba(0,0,0,0.05)',
            transform: selected ? 'scale(1.02)' : 'scale(1)'
        }}>
            {!data.code.includes('#') ? (
                <div style={NODE_STYLES.code}>{data.code}</div>
            ) : null}
            <div style={NODE_STYLES.header}>
                <div style={NODE_STYLES.emoji}>
                    {data.emoji || '📚'}
                </div>
                <div>
                    <div style={NODE_STYLES.title}>{data.name}</div>
                </div>
            </div>

            <Handle
                type="target"
                position={Position.Top}
                style={{
                    width: '12px',
                    height: '12px',
                    backgroundColor: '#D84938',
                    opacity: hasConnections ? 1 : 0,
                    transform: 'translateX(-5px)',
                    zIndex: 10
                }}
            />
            <Handle
                type="source"
                position={Position.Bottom}
                style={{
                    width: '12px',
                    height: '12px',
                    backgroundColor: '#3B82F6',
                    opacity: hasConnections ? 1 : 0,
                    transform: 'translateX(-5px)',
                    zIndex: 10
                }}
            />
        </div>
    );
});

// **Componente para títulos de semestre**
const SemesterGroup = memo(({ data, faded }) => {
    return (
        <div style={{
            width: '97%',
            padding: '12px 16px',
            background: 'linear-gradient(to right, #94c0e4, #5781a4)',
            color: 'white',
            fontWeight: '600',
            fontSize: '16px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            borderTopLeftRadius: '10px',
            borderTopRightRadius: '10px',
            zIndex: 1,
            opacity: faded ? 0.3 : 1
        }}>
            {data.label}
        </div>
    );
});

// **Función para crear nodos y conexiones**
const createFlowElements = (curriculum) => {
    const nodes = [];
    const edges = [];
    let yPosition = 40;

    const containerWidth = 1110;
    const nodeWidth = 180;
    const horizontalSpacing = 35;
    const verticalSpacing = 120;
    const maxNodesPerRow = 5;

    Object.entries(curriculum).forEach(([semester, courses], semesterIndex) => {
        const coursesArray = Object.entries(courses);
        const rowCount = Math.ceil(coursesArray.length / maxNodesPerRow);
        const containerHeight = 80 + (rowCount * verticalSpacing);

        // **Contenedor del semestre**
        nodes.push({
            id: semester,
            type: 'group',
            position: { x: 50, y: yPosition },
            style: {
                width: containerWidth,
                height: containerHeight,
                backgroundColor: COLORS.semesters[semesterIndex],
                borderRadius: '12px',
                border: '1px solid #E2E8F0',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
                overflow: 'hidden'
            },
            data: { label: SEMESTER_TITLES[semester] }
        });

        // **Posicionamiento de cursos**
        let row = 0;
        let col = 0;

        coursesArray.forEach(([code, courseData]) => {
            const xPos = 20 + (col * (nodeWidth + horizontalSpacing));
            const yPos = 60 + (row * verticalSpacing);

            nodes.push({
                id: code,
                type: 'uniform',
                position: { x: xPos, y: yPos },
                parentId: semester,
                extent: 'parent',
                data: {
                    name: courseData[0],
                    code: code,
                    area: courseData[2] || 0
                },
                style: {
                    backgroundColor: COLORS.areas[courseData[2]] || '#FFFFFF',
                    zIndex: 2
                }
            });

            // **Conexiones con prerequisitos**
            courseData[1].forEach(preReq => {
                edges.push({
                    id: `${preReq}-${code}`,
                    source: preReq,
                    target: code,
                    style: {
                        stroke: '#3B82F6',
                        strokeWidth: 0,
                        zIndex: 1000
                    },
                    zIndex: 1000,
                    markerEnd: {
                        type: 'arrowclosed',
                        color: '#2500ce'
                    }
                });
            });

            col++;
            if (col >= maxNodesPerRow) {
                col = 0;
                row++;
            }
        });

        yPosition += containerHeight + 30;
    });

    return { nodes, edges };
};

// **Componente principal**
export default function CurriculumFlow() {
    const pathParts = window.location.pathname.split('/');
    const carreraParam = pathParts.length > 1 ? pathParts[2] : null;
    const carrera = carreraParam ? carreraParam.toLowerCase() : 'isi';

    const [selectedNode, setSelectedNode] = useState(null);
    const [relatedNodes, setRelatedNodes] = useState(new Set());

    // **Selección del currículo según la URL**
    const getCurriculumData = useCallback(() => {
        switch (carrera) {
            case 'itecad':
                return carrera1;
            case 'isi':
                return carrera2;
            case 'topo':
                return carrera3;
            case 'iciagro':
                return carrera4;
            case 'ictp':
                return carrera5;
            default:
                return carrera1;
        }
    }, [carrera]);

    const { nodes: initialNodes, edges: initialEdges } = createFlowElements(getCurriculumData().mesh);
    const [nodes] = useState(initialNodes);
    const [edges, setEdges] = useState(initialEdges);

    // **Manejo de clic en nodos**
    const handleNodeClick = useCallback((_, node) => {
        const related = new Set();
        related.add(node.id);

        // **Encontrar requisitos y dependencias**
        edges.forEach(edge => {
            if (edge.target === node.id) related.add(edge.source);
            if (edge.source === node.id) related.add(edge.target);
        });

        setSelectedNode(node.id);
        setRelatedNodes(related);

        // **Actualizar estilos de conexiones**
        setEdges(edges.map(edge => ({
            ...edge,
            hidden: !(edge.source === node.id || edge.target === node.id),
            animated: edge.source === node.id || edge.target === node.id,
            style: {
                ...edge.style,
                stroke: edge.source === node.id || edge.target === node.id ? '#2500ce' : '#E2E8F0',
                strokeWidth: edge.source === node.id || edge.target === node.id ? 3 : 1,
                opacity: edge.source === node.id || edge.target === node.id ? 1 : 0.2,
                zIndex: edge.source === node.id || edge.target === node.id ? 1000 : 1
            }
        })));
    }, [edges]);

    // **Restablecer al hacer clic en el fondo**
    const handlePaneClick = useCallback(() => {
        setSelectedNode(null);
        setRelatedNodes(new Set());
        setEdges(edges.map(edge => ({
            ...edge,
            hidden: false,
            animated: false,
            style: {
                ...edge.style,
                stroke: '#3B82F6',
                strokeWidth: 0,
                opacity: 1,
                zIndex: 1
            }
        })));
    }, [edges]);

    // **Tipos de nodos personalizados**
    const nodeTypes = {
        uniform: (props) => <UniformNode
            {...props}
            selected={selectedNode ? relatedNodes.has(props.id) : true}
        />,
        group: (props) => <SemesterGroup
            {...props}
            faded={selectedNode !== null && !Array.from(relatedNodes).some(nodeId =>
                nodes.find(n => n.id === nodeId)?.parentId === props.id
            )}
        />
    };

    return (
        <div style={{
            width: '100vw',
            height: '90vh',
            backgroundColor: '#F8FAFC',
            fontFamily: 'Arial, sans-serif'
        }}>
            <CarreraTitle nombre={getCurriculumData().name || "Carrera no especificada"} />

            <div style={{
                height: 'calc(90vh - 120px)',
                position: 'relative',
                backgroundColor: selectedNode ? 'rgba(248, 250, 252, 0.5)' : '#F8FAFC',
                transition: 'background-color 0.3s ease'
            }}>

                {/* Imagen de fondo */}
                <img
                    src="/public/img/SedeRegionalBrunca.png"
                    alt="Fondo"
                    style={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        height: '20%',
                        objectFit: 'cover', // Ajusta la imagen
                        opacity: 0.3, // Hazla semi-transparente
                        zIndex: 0, // Detrás de todo
                        filter: 'blur(1px)' // Opcional: difuminado suave
                    }}
                />

                <ReactFlow
                    nodes={nodes.map(node => ({
                        ...node,
                        style: {
                            ...node.style,
                            opacity: selectedNode
                                ? (relatedNodes.has(node.id) ? 1 : 0.3)
                                : 1
                        }
                    }))}
                    edges={edges}
                    nodeTypes={nodeTypes}
                    onNodeClick={handleNodeClick}
                    onPaneClick={handlePaneClick}
                    fitView
                    nodesDraggable={false}
                    elementsSelectable={true}
                    connectionRadius={100}
                    snapToGrid={true}
                    snapGrid={[15, 15]}
                    edgesUpdatable={false}
                    edgesFocusable={false}
                    elevateEdgesOnSelect={true}
                    elevateNodesOnSelect={true}
                >
                    <Controls />
                </ReactFlow>
            </div>
        </div>
    );
}